'use strict';

// external imports

// shared imports
import LocalizableError from '~/shared/src/base/LocalizableError';
import metaModel from "~/shared/src/utilities/metaModel";
import Status from "~/shared/src/mm/Status";

// backend imports


// type definitions
export type PropertyTypeAttributes = {
    id? : number,
    name? : string,
    statusId? : number,
    origin?: string,
    module?: string,
    uuid? : string,
    modifierUserId? : string,
    modificationTime? : NDate,
    validFromTime? : NDate,
    validToTime? : NDate
};

export type PropertyTypeAttributesCollection = Array<PropertyTypeAttributes>;

export type PropertyTypeAttributesRequired = keyof Pick<PropertyTypeAttributes, 'id' | 'name' | 'statusId'>;

export type PropertyTypeAttributesSettable = Pick<PropertyTypeAttributes, 'name' | 'statusId' | 'origin' | 'module' >;

/**
 * The PropertyType class.
 */
class PropertyType {

    /**
     * The unique Id counter for PropertyTypes
     */
    static uniqueIdCount = -1;


    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ID of the PropertyType
     */
    readonly id : number;

    /**
     * The Name of the PropertyType
     */
    protected _name : string = '';
    get name() {
        return this._name;
    }

    set name( value : string ) {
        this.validateName( value );

        this.handleModified( 'name', value );

        this._name = value;

        // TODO: how to handle UUID's update (PropertyType, PropertySets, ReferencePropertySets)
    }

    /**
     * The StatusID of the PropertyType
     */
    protected _statusId : number;
    get statusId() : number {
        return this._statusId;
    }

    set statusId( value : number ) {
        this.validateStatusId( value );

        this.handleModified( 'statusId', value );

        this._statusId = value;
    }

    /**
     * The origin of this
     */
    protected _origin: string = 'FSZ';
    get origin(): string {
        return this._origin;
    }

    set origin( value : string ) {
        this.validateOrigin( value );

        this.handleModified( 'origin', value );

        this._origin = value;
    }

    /**
     * The module this belongs to
     * @protected
     */
    protected _module: string = 'base';
    get module(): string {
        return this._origin;
    }

    set module( value : string ) {
        this.validateModule( value );

        this.handleModified( 'module', value );

        this._module = value;
    }

    /**
     * The unique spoken id of the PropertyType
     */
    readonly uuid : string = '';

    /**
     * The UserID of the last modifier of the PropertyType
     */
    readonly modifierUserId : string = '';

    /**
     * The last modification date of the PropertyType
     */
    readonly modificationTime : NDate = null;

    /**
     * The valid from date of a History-PropertyType
     */
    readonly validFromTime : NDate = null;

    /**
     * The valid to date of a History-PropertyType
     */
    readonly validToTime : NDate = null;


    /*** ALIASES ***********************************************************************************************************************/

    get status() : Status {
        return metaModel[ this._schema ].getStatus( this._statusId )!;
    }

    get label() : string {
        return this._name;
    }


    /*************************************************************************************************************************************/

    /**
     * The database schema this Item belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * Map containing the modified attributes with their initial values
     */
    protected _attributesModified : Map<keyof PropertyTypeAttributesSettable, AttributeValue>;
    get attributesModified() {
        return this._attributesModified;
    }

    /**
     * The constructor.
     *
     * @param attributes                An object containing attribute-value pairs for this
     * @param schema                    The database schema the Item belongs to
     */
    constructor( attributes : PropertyTypeAttributes, schema : Schema = global.schema ) {

        if ( attributes.id && attributes.id < 1 ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attributes.validation.id.invalid' );
        }

        this._schema = schema;

        if ( attributes.id ) {
            this.id = attributes.id;
        }
        else {
            this.id = PropertyType.uniqueIdCount--;
        }

        if ( attributes.statusId ) {
            this._statusId = attributes.statusId;
        }
        else {
            this._statusId = metaModel[ schema ].getStatus( 'active' )!.id;
        }

        if ( attributes.origin ) {
            this._origin = attributes.origin;
        }

        if ( attributes.module ) {
            this._module = attributes.module;
        }

        if ( attributes.name ) {
            this._name = attributes.name;
        }

        if ( attributes.uuid ) {
            this.uuid = attributes.uuid;
        }

        if ( attributes.modifierUserId ) {
            this.modifierUserId = attributes.modifierUserId;
        }
        if ( attributes.modificationTime ) {
            this.modificationTime = attributes.modificationTime;
        }

        if ( attributes.validFromTime ) {
            this.validFromTime = attributes.validFromTime;
        }
        if ( attributes.validToTime ) {
            this.validToTime = attributes.validToTime;
        }

        this._attributesModified = new Map();
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param includeAttributes   The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : PropertyTypeAttributes {
        // required attributes
        const retVal : PropertyTypeAttributes = {
            id: this.id
        };

        if ( includeAttributes === 'all' || this._attributesModified.has( 'name' ) ) {
            retVal.name = this._name;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'statusId' ) ) {
            retVal.statusId = this._statusId;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'origin' ) ) {
            retVal.origin = this._origin;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'module' ) ) {
            retVal.module = this._module;
        }

        // not settable attributes
        if ( includeAttributes === 'all' ) {
            retVal.uuid = this.uuid;
            retVal.modifierUserId = this.modifierUserId;

            // only active attributes
            if ( !this.isHistorized() ) {
                retVal.modificationTime = this.modificationTime;
            }

            // only history attributes
            else {
                retVal.validFromTime = this.validFromTime;
                retVal.validToTime = this.validToTime;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        return `PropertyType '${this.name}' (ID: ${this.id})`;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() {
        if ( this._attributesModified.has( 'name' ) ) {
            this._name = this._attributesModified.get( 'name' ) as string;
        }

        if ( this._attributesModified.has( 'statusId' ) ) {
            this._statusId = this._attributesModified.get( 'statusId' ) as number;
        }

        this._attributesModified = new Map();
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     */
    clone( attributes : PropertyTypeAttributesSettable = {} ) : PropertyType {
        const clone : PropertyType = Object.assign( {}, this );

        // apply data
        if ( attributes.name ) {
            clone.name = attributes.name;
        }

        if ( attributes.statusId ) {
            clone.statusId = attributes.statusId;
        }

        return clone;
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        return this.id < 0;
    }

    /**
     * Checks if this is historized.
     */
    isHistorized() : boolean {
        return this.validFromTime !== null;
    }

    /**
     * Checks if this is deleted.
     */
    isDeleted() : boolean {
        return this._statusId === metaModel[ this._schema ].getStatus( 'history' )?.id || this._statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id;
    }

    /**
     * Checks if this or an EnumProperty or a Property of this is modified.
     */
    isModified() : boolean {
        return this._attributesModified.size > 0;
    }

    /**
     * Checks if the given attribute is modified or not.
     *
     * @param attribute        The attribute to check if it is modified
     */
    isAttributeModified( attribute : keyof PropertyTypeAttributesSettable ) : boolean {
        return this._attributesModified.has( attribute );
    }

    /**
     * Handles the modified attributes list.
     *
     * @param attribute         The attribute
     * @param value             The value for the attribute
     */
    protected handleModified( attribute : keyof PropertyTypeAttributesSettable, value : AttributeValue ) {
        // set as modified
        if ( !this._attributesModified.has( attribute ) && value !== this[ attribute ] ) {
            this._attributesModified.set( attribute, this[ attribute ] );
        }

        // unset as modified
        else if ( this._attributesModified.has( attribute ) && value === this._attributesModified.get( attribute ) ) {
            this._attributesModified.delete( attribute );
        }
    }

    protected checkStatus() {
        if ( this.isDeleted() ) {
            throw new LocalizableError(
                'YN.base.dataObject.propertyType.attributes.validation.couldNotChangeDeleted'
            );
        }
    }

    /**
     * Validates the given statusId
     */
    protected validateStatusId( statusId : number ) {
        if ( !metaModel[ this._schema ].getStatus( statusId ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.statusId.notExists' );
        }

        if (
            (
                statusId === metaModel[ this._schema ].getStatus( 'history' )?.id
                || statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id
            )
            && (
                this._attributesModified.size > 1
                || ( this._attributesModified.size === 1 && !this._attributesModified.has( 'statusId' ) )
            )
        ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.attributes.validation.couldNotDeleteChanged'
            );
        }
    }

    /**
     * Validates the origin for this
     */
    protected validateOrigin( origin : string ) {
        this.checkStatus();

        if ( !origin.match( /^[A-Z]+$/ ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.origin.isNotUppercase' );
        }
    }

    /**
     * Validates the module for this
     */
    protected validateModule( module : string ) {
        this.checkStatus();
    }

    /**
     * Validates the name for this
     */
    protected validateName( name : string ) {
        this.checkStatus();

        const propertyType = metaModel[ this._schema ].getPropertyType( name );

        if ( propertyType && propertyType.id !== this.id ) {
            throw new LocalizableError( 'YN.base.dataObject.propertyType.attribute.validation.name.isNotUnique' );
        }
    }

}

export default PropertyType;
